import { FormEvent, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { apiError } from '../../services/api';
import { useApi } from '../../hooks/useApi';
import CountrySelect from '../../components/ui/CountrySelect';
import { Card, Field, FormActions, Notice, PageHeader } from '../../components/ui/Page';

const LOCALISED = new Set(['IN', 'US', 'GB', 'SG', 'AE']);

export default function DocumentCountryFormPage() {
  const navigate = useNavigate();
  const { data } = useApi<{ countries: { code: string; name: string }[] }>('/admin/documents');
  const [country, setCountry] = useState({ code: '', name: '' });
  const [start, setStart] = useState<'pack' | 'empty'>('pack');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const configured = useMemo(() => Object.fromEntries((data?.countries || []).map((c) => [c.code, 'Already configured — opens its checklist'])), [data]);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!country.code) return setError('Choose a country.');
    if (configured[country.code]) return navigate(`/documents/${country.code}`);
    setSaving(true);
    setError(null);
    try {
      if (start === 'pack') {
        await api.post(`/admin/documents/country/${country.code}/seed`, { countryName: country.name });
        navigate(`/documents/${country.code}`, { replace: true });
      } else {
        navigate(`/documents/${country.code}/new?name=${encodeURIComponent(country.name)}`, { replace: true });
      }
    } catch (err) {
      setError(apiError(err, 'Could not add the country.'));
    } finally {
      setSaving(false);
    }
  };

  const option = (value: 'pack' | 'empty', title: string, body: string) => (
    <label
      className={`flex cursor-pointer gap-3 rounded-xl p-3.5 transition ${start === value ? 'bg-[#fdf2f8]' : 'bg-white hover:bg-[#fffafc]'}`}
      style={{ border: `1.5px solid ${start === value ? '#d6006e' : '#f0cfdd'}` }}
    >
      <input type="radio" name="start" className="mt-0.5 accent-[#d6006e]" checked={start === value} onChange={() => setStart(value)} />
      <span>
        <span className="block text-[13px] font-semibold text-[#3b0a22]">{title}</span>
        <span className="mt-0.5 block text-xs leading-relaxed text-[#9a6b82]">{body}</span>
      </span>
    </label>
  );

  return (
    <div className="max-w-2xl">
      <PageHeader crumbs={[{ label: 'Documents', to: '/documents' }, { label: 'Add country' }]} title="Add country" description="Enable Micus for a new country by giving it a document checklist." />
      <Card>
        <form onSubmit={onSubmit} className="space-y-4">
          <Field label="Country" htmlFor="d-country">
            <CountrySelect id="d-country" value={country.code} highlight={configured} onChange={(code, name) => setCountry({ code, name })} />
          </Field>
          <div className="space-y-2">
            <p className="mz-label">Start from</p>
            {option(
              'pack',
              'Standard Micus credit pack (recommended)',
              `Financial, legal, KYC and business documents with AI checks for every item, filtered by rating track.${
                country.code && LOCALISED.has(country.code) ? ' Uses local document names for this country.' : ' Uses generic names you can rename.'
              }`,
            )}
            {option('empty', 'Empty checklist', 'Add every document yourself.')}
          </div>
          {error && <Notice tone="error">{error}</Notice>}
          <FormActions>
            <button type="button" className="mz-btn-ghost" onClick={() => navigate('/documents')}>
              Cancel
            </button>
            <button type="submit" disabled={saving || !country.code} className="mz-btn-primary">
              {saving ? 'Creating…' : configured[country.code] ? 'Open checklist' : 'Create checklist'}
            </button>
          </FormActions>
        </form>
      </Card>
    </div>
  );
}
